import React, { useMemo } from "react";
import { Button, Col, Dropdown, Input, Row, Select, Space, Table, Tabs, Typography } from "antd";
import dayjs from "dayjs";
import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CopyableText from "components/common/CopyableText";
import {
  formatCurrency,
  formatAmount,
  formatLabel,
  getStatusTag,
  isFreePlanRecord,
  PLAN_TYPE_OPTIONS,
  SUBSCRIBER_TYPE_OPTIONS,
} from "./subscriptionConstants";

const { Text } = Typography;

const normalizeStatus = (value) => String(value || "").toUpperCase();

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

const STATUS_CARDS = [
  { key: "ACTIVE", label: "Active", value: "ACTIVE", color: "#52c41a", bg: "#f6ffed" },
  { key: "PENDING", label: "Pending", value: "PENDING", color: "#fa8c16", bg: "#fff7e6" },
  { key: "TRIALING", label: "Trialing", value: "TRIALING", color: "#1677ff", bg: "#e6f4ff" },
  { key: "SUSPENDED", label: "Suspended", value: "SUSPENDED", color: "#722ed1", bg: "#f9f0ff" },
  { key: "EXPIRED", label: "Expired", value: "EXPIRED", color: "#fa541c", bg: "#fff2e8" },
  { key: "PAST_DUE", label: "Past due", value: "PAST_DUE", color: "#eb2f96", bg: "#fff0f6" },
  { key: "CANCELLED", label: "Cancelled", value: "CANCELLED", color: "#ff4d4f", bg: "#fff1f0" },
  { key: "CANCELED", label: "Canceled", value: "CANCELED", color: "#ff4d4f", bg: "#fff1f0" },
];

const getStatusCounts = (items = []) => {
  const counts = {
    total: Array.isArray(items) ? items.length : 0,
    ACTIVE: 0,
    PENDING: 0,
    TRIALING: 0,
    SUSPENDED: 0,
    EXPIRED: 0,
    PAST_DUE: 0,
    CANCELLED: 0,
    CANCELED: 0,
  };

  (Array.isArray(items) ? items : []).forEach((item) => {
    const status = normalizeStatus(item?.status);
    if (status === "ACTIVE") counts.ACTIVE += 1;
    else if (status === "PENDING") counts.PENDING += 1;
    else if (status === "TRIALING") counts.TRIALING += 1;
    else if (status === "SUSPENDED") counts.SUSPENDED += 1;
    else if (status === "EXPIRED") counts.EXPIRED += 1;
    else if (status === "PAST_DUE") counts.PAST_DUE += 1;
    else if (status === "CANCELLED") counts.CANCELLED += 1;
    else if (status === "CANCELED") counts.CANCELED += 1;
  });

  return counts;
};

const StatCard = ({ label, value, color, bg, active = false, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick?.();
    }}
    style={{ cursor: "pointer" }}
  >
    <div
      style={{
        borderLeft: `5px solid ${color}`,
        backgroundColor: bg,
        borderRadius: 12,
        padding: 12,
        minHeight: 72,
        boxShadow: active ? `0 0 0 1px ${color}40 inset` : "none",
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    </div>
  </div>
);

export default function SubscriptionManagementTabs({
  activeTab,
  onTabChange,
  plans,
  plansTotal,
  plansLoading,
  planPage,
  planPageSize,
  planFilters,
  onPlanFiltersChange,
  onPlanPageChange,
  onPlanRefresh,
  onOpenPlanDrawer,
  onOpenPlanRawDrawer,
  onDeletePlan,
  subscriptions,
  subscriptionsTotal,
  subscriptionsLoading,
  subscriptionPage,
  subscriptionPageSize,
  subscriptionFilters,
  onSubscriptionFiltersChange,
  onSubscriptionPageChange,
  onSubscriptionRefresh,
  onOpenSubscriptionDrawer,
  onOpenSubscriptionRawDrawer,
  onCancelSubscription,
}) {
  const planStatusCounts = useMemo(() => getStatusCounts(plans), [plans]);
  const subscriptionStatusCounts = useMemo(() => getStatusCounts(subscriptions), [subscriptions]);

  const planColumns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 320,
        ellipsis: true,
        render: (value, record) => (
          <span
            className="user-id-cell"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e?.target?.closest?.(".ant-typography-copy")) return;
              onOpenPlanRawDrawer(record);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onOpenPlanRawDrawer(record);
            }}
            style={{ cursor: "pointer" }}
          >
            <CopyableText value={value} showTooltip={false} stopPropagation={false} />
          </span>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (value) => <Text strong>{value || "-"}</Text>,
      },
      {
        title: "Type",
        dataIndex: "subscriptionType",
        key: "subscriptionType",
        render: (value) => getStatusTag(value),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => getStatusTag(value),
      },
      {
        title: "Monthly",
        dataIndex: "monthlyAmount",
        key: "monthlyAmount",
        render: (value) => formatAmount(value),
      },
      {
        title: "Annual",
        dataIndex: "annualAmount",
        key: "annualAmount",
        render: (value) => formatAmount(value),
      },
      {
        title: "Actions",
        key: "actions",
        width: 88,
        render: (_, record) => (
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "edit",
                    label: "Edit",
                    icon: <EditOutlined />,
                    onClick: () => onOpenPlanDrawer(record),
                  },
                  {
                    key: "raw",
                    label: "View",
                    icon: <EyeOutlined />,
                    onClick: () => onOpenPlanRawDrawer(record),
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    danger: true,
                    icon: <DeleteOutlined />,
                    onClick: () => onDeletePlan(record),
                  },
                ],
              }}
            >
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          </span>
        ),
      },
    ],
    [onDeletePlan, onOpenPlanDrawer, onOpenPlanRawDrawer],
  );

  const subscriptionColumns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 320,
        ellipsis: true,
        render: (value, record) => (
          <span
            className="user-id-cell"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (e?.target?.closest?.(".ant-typography-copy")) return;
              onOpenSubscriptionRawDrawer(record);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onOpenSubscriptionRawDrawer(record);
            }}
            style={{ cursor: "pointer" }}
          >
            <CopyableText value={value} showTooltip={false} stopPropagation={false} />
          </span>
        ),
      },
      {
        title: "Subscriber",
        key: "subscriber",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{formatLabel(record?.subscriberType)}</Text>
          </Space>
        ),
      },
      {
        title: "Plan",
        key: "plan",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text>{record?.subscriptionPlanName || "-"}</Text>
          </Space>
        ),
      },
      {
        title: "Amount",
        key: "amount",
        render: (_, record) => formatCurrency(record?.amount, record?.currency),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => getStatusTag(value),
      },
      {
        title: "Payment",
        key: "payment",
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text>{isFreePlanRecord(record) ? "Free" : formatLabel(record?.subscriptionPaymentType)}</Text>
            {!isFreePlanRecord(record) ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatLabel(record?.paymentStatus)}
              </Text>
            ) : null}
          </Space>
        ),
      },
      { title: "Start", dataIndex: "startDate", key: "startDate", render: (value) => formatDateTime(value) },
      { title: "End", dataIndex: "endDate", key: "endDate", render: (value) => formatDateTime(value) },
      {
        title: "Actions",
        key: "actions",
        width: 88,
        render: (_, record) => {
          const currentStatus = String(record?.status || "").toUpperCase();
          const isCancelled = ["CANCELLED", "CANCELED"].includes(currentStatus);

          return (
            <span
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "raw",
                      label: "View",
                      icon: <EyeOutlined />,
                      onClick: () => onOpenSubscriptionRawDrawer(record),
                    },
                    {
                      key: "edit",
                      label: "Edit",
                      icon: <EditOutlined />,
                      onClick: () => onOpenSubscriptionDrawer(record),
                    },
                  ...(isCancelled
                      ? []
                      : [
                          {
                            key: "cancel",
                            label: "Cancel",
                            danger: true,
                            icon: <CloseCircleOutlined />,
                            onClick: () => onCancelSubscription(record),
                          },
                        ]),
                  ],
                }}
              >
                <Button type="text" icon={<EllipsisOutlined />} />
              </Dropdown>
            </span>
          );
        },
      },
    ],
    [onCancelSubscription, onOpenSubscriptionDrawer, onOpenSubscriptionRawDrawer],
  );

  const planTab = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
        {STATUS_CARDS.map((card) => (
          <Col key={`plan-${card.key}`} xs={12} sm={6} lg={3}>
            <StatCard
              label={card.label}
              value={card.value ? planStatusCounts[card.key] ?? 0 : planStatusCounts.total}
              color={card.color}
              bg={card.bg}
              active={normalizeStatus(planFilters.status || "") === card.key}
              onClick={() => onPlanFiltersChange({ status: card.value })}
            />
          </Col>
        ))}
      </Row>

      <Row gutter={18} className="align-items-end">
        <Col xs={10} lg={6}>
          <Input
            allowClear
            placeholder="Search by code"
            value={planFilters.code}
            onChange={(e) => onPlanFiltersChange({ code: e.target.value })}
            style={{ borderRadius: 12, height: 32 }}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <Select
            allowClear
            placeholder="Type"
            value={planFilters.type}
            onChange={(value) => onPlanFiltersChange({ type: value })}
            options={PLAN_TYPE_OPTIONS}
            style={{ width: "100%", minWidth: 160 }}
          />
        </Col>
        <Col xs={24} lg={12}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Button icon={<ReloadOutlined />} onClick={onPlanRefresh}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenPlanDrawer()}>
              Add plan
            </Button>
          </div>
        </Col>
      </Row>

      <div style={{ width: "100%", overflowX: "hidden" }}>
        <Table
          rowKey="id"
          dataSource={plans}
          columns={planColumns}
          size="small"
          bordered
          className="custom-table"
          tableLayout="fixed"
          scroll={{ x: 1400 }}
          loading={plansLoading}
          onRow={(record) => ({
            onClick: (e) => {
              if (
                e?.target?.closest?.("button") ||
                e?.target?.closest?.("a") ||
                e?.target?.closest?.("input") ||
                e?.target?.closest?.(".ant-select") ||
                e?.target?.closest?.(".ant-typography-copy")
              ) {
                return;
              }
              onOpenPlanRawDrawer(record);
            },
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: planPage + 1,
            pageSize: planPageSize,
            total: plansTotal,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 30, 50, 100, 200, 500],
            onChange: onPlanPageChange,
          }}
        />
      </div>
    </Space>
  );

  const subscriptionTab = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
        {STATUS_CARDS.map((card) => (
          <Col key={`subscription-${card.key}`} xs={12} sm={6} lg={3}>
            <StatCard
              label={card.label}
              value={card.value ? subscriptionStatusCounts[card.key] ?? 0 : subscriptionStatusCounts.total}
              color={card.color}
              bg={card.bg}
              active={normalizeStatus(subscriptionFilters.status || "") === card.key}
              onClick={() => onSubscriptionFiltersChange({ status: card.value })}
            />
          </Col>
        ))}
      </Row>

      <Row gutter={[8, 8]} className="align-items-end">
        <Col xs={24} sm={8} lg={4}>
          <div style={{ minWidth: 0 }}>
            <Select
              allowClear
              placeholder="Subscriber type"
              value={subscriptionFilters.subscriberType}
              onChange={(value) => onSubscriptionFiltersChange({ subscriberType: value })}
              options={SUBSCRIBER_TYPE_OPTIONS}
              style={{ width: "100%" }}
            />
          </div>
        </Col>
        <Col xs={24} sm={8} lg={4}>
          <Input
            allowClear
            placeholder="Subscriber ID"
            value={subscriptionFilters.subscriberId}
            onChange={(e) => onSubscriptionFiltersChange({ subscriberId: e.target.value })}
            style={{ borderRadius: 12, height: 32 }}
          />
        </Col>
        <Col xs={24} sm={8} lg={16}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Button icon={<ReloadOutlined />} onClick={onSubscriptionRefresh}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenSubscriptionDrawer()}>
              Add subscription
            </Button>
          </div>
        </Col>
      </Row>

      <div style={{ width: "100%", overflowX: "hidden" }}>
        <Table
          rowKey="id"
          dataSource={subscriptions}
          columns={subscriptionColumns}
          size="small"
          bordered
          className="custom-table"
          tableLayout="fixed"
          scroll={{ x: 1200 }}
          loading={subscriptionsLoading}
          onRow={(record) => ({
            onClick: (e) => {
              if (
                e?.target?.closest?.("button") ||
                e?.target?.closest?.("a") ||
                e?.target?.closest?.("input") ||
                e?.target?.closest?.(".ant-select") ||
                e?.target?.closest?.(".ant-typography-copy")
              ) {
                return;
              }
              onOpenSubscriptionRawDrawer(record);
            },
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: subscriptionPage + 1,
            pageSize: subscriptionPageSize,
            total: subscriptionsTotal,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 30, 50, 100, 200, 500],
            onChange: onSubscriptionPageChange,
          }}
        />
      </div>
    </Space>
  );

  return (
    <div className="container-fluid p-2" style={{ maxWidth: 1400, margin: "0 auto" }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0, flex: "1 1 280px" }}>
            <WidgetSectionTitle
              icon="credit-card"
              transform="shrink-2"
              title="Subscriptions"
              subtitle="Manage subscription plans and live subscriptions"
              className="mb-0 mt-3"
            />
          </div>

        </div>

        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          items={[
            { key: "plans", label: `Plans (${plansTotal})`, children: planTab },
            { key: "subscriptions", label: `Subscriptions (${subscriptionsTotal})`, children: subscriptionTab },
          ]}
        />
      </Space>
    </div>
  );
}
